import { Button, Chip, Typography } from "@mui/material";
import { useGetRolesQuery, useGetAgentsQuery } from "../../redux/api/rolesApi";
import { CancelOutlined, CheckCircleOutline } from "@mui/icons-material";

// Shared Permission Cell Component
const PermissionCell = ({ value }) => {
  const hasPermission = value === true || value === 1;
  return (
    <Chip
      label={hasPermission ? "Yes" : "No"}
      size="small"
      color={hasPermission ? "success" : "error"}
      icon={
        hasPermission ? (
          <CheckCircleOutline fontSize="small" />
        ) : (
          <CancelOutlined fontSize="small" />
        )
      }
      sx={{
        fontWeight: 500,
        width: 60,
        "& .MuiChip-icon": {
          marginLeft: "4px",
        },
      }}
    />
  );
};

// Helper function for role names
export function getRoleName(roleId, roles) {
  const matchedRole = roles && roles.find((role) => role.roleId === roleId);
  return matchedRole ? matchedRole.roleName : `Role ${roleId}`;
}


// Roles Table Columns
export const ROLES_PERMISSION_COLUMNS = (onEdit) => [
  {
    accessorKey: "roleId",
    header: "Role",
    size: 120,
    Cell: ({ cell }) => {
      const { data: roles } = useGetRolesQuery();
      const roleName = getRoleName(cell.getValue(), roles);
      return (
        <Typography variant="body2" fontWeight={500}>
          {roleName}
        </Typography>
      );
    },
  },
  {
    accessorKey: "addProperty",
    header: "Add Property",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewProperty",
    header: "View Property",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "updateProperty",
    header: "Update Property",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "propertylocation",
    header: "Property Location",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "ownercontact",
    header: "Owner Contact",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "addLead",
    header: "Add Lead",
    size: 90,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "updateLead",
    header: "Update Lead",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewpool",
    header: "View Leads Pool",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "assignlead",
    header: "Assign Lead",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewlead",
    header: "View Lead",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewalllead",
    header: "View Agency Leads",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "uploadleads",
    header: "Upload Leads",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "genVoucher",
    header: "Generate Voucher",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewContacts",
    header: "View Contacts",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewAccounts",
    header: "View Accounts",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "addstaff",
    header: "Add Staff",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "viewalltask",
    header: "View All Tasks",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "assigntask",
    header: "Assign Task",
    size: 110,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    header: "Actions",
    size: 100,
    Cell: ({ row }) => (
      <Button
        variant="outlined"
        size="small"
        onClick={() => onEdit(row.original)}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '8px'
        }}
      >
        Edit
      </Button>
    ),
  },
];

// Agents/Leads Permissions Table Columns
export const LEADS_PERMISSION_COLUMNS = (onEdit) => [
  {
    accessorKey: "role",
    header: "Role",
    size: 120,
    Cell: ({ cell }) => {
      const { data: roles } = useGetRolesQuery();
      const roleName = getRoleName(cell.getValue(), roles);
      return (
        <Typography variant="body2" fontWeight={500}>
          {roleName}
        </Typography>
      );
    },
  },
  {
    accessorKey: "firstname",
    header: "First Name",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "lastname",
    header: "Last Name",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "email",
    header: "Email",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "whatsappNo",
    header: "WhatsApp",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "membershipId",
    header: "Membership ID",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Lead Information
  {
    accessorKey: "leadtype",
    header: "Lead Type",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "leadTime",
    header: "Lead Time",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "leadTag",
    header: "Lead Tag",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "leadStatus",
    header: "Lead Status",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "leadSerialNo",
    header: "Lead Serial No",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Professional Information
  {
    accessorKey: "companyName",
    header: "Company",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "designation",
    header: "Designation",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "budget",
    header: "Budget",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "location",
    header: "Location",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Property Information
  {
    accessorKey: "propertydata",
    header: "Property Data",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "propertyRefNo",
    header: "Property Ref No",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "propertyType",
    header: "Property Type",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "societyName",
    header: "Society Name",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "fileNo",
    header: "File No",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "block",
    header: "Block",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "plotNo",
    header: "Plot No",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "plotSize",
    header: "Plot Size",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "additionalNo",
    header: "Additional No",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Source Information
  {
    accessorKey: "source",
    header: "Source",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "medium",
    header: "Medium",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "priority",
    header: "Priority",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Additional Information
  {
    accessorKey: "history",
    header: "History",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "lastUpdatedDate",
    header: "Last Updated",
    size: 120,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "type",
    header: "Type",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },
  {
    accessorKey: "moreinfo",
    header: "More Info",
    size: 100,
    Cell: ({ cell }) => <PermissionCell value={cell.getValue()} />,
  },

  // Actions
  {
    header: "Actions",
    size: 100,
    Cell: ({ row }) => (
      <Button
        variant="outlined"
        size="small"
        onClick={() => onEdit(row.original)}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '8px'
        }}
      >
        Edit
      </Button>
    ),
  },
];